import { GetLayout } from '@shared/frontend';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { LayoutValues } from '@shared/frontend';

/** Layout context type definition. */
interface LayoutContextType {
  layout: LayoutValues;
  scrollContentToTop: () => void;
  registerContentElement: (element: HTMLElement | null) => void;
}

/** Layout context instance. */
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

/**
 * @param root0
 * @param root0.children The child components to be wrapped by the LayoutProvider.
 * @returns A JSX element wrapping children with LayoutContext provider.
 */
export function LayoutProvider({ children }: { children: ReactNode }) {
  const [layout, setLayout] = useState<LayoutValues>(GetLayout());
  const contentRef = useRef<HTMLElement | null>(null);

  // Update layout on window resize
  useEffect(() => {
    const handleResize = () => setLayout(GetLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollContentToTop = useCallback(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const registerContentElement = useCallback((element: HTMLElement | null) => {
    contentRef.current = element;
  }, []);

  // Memoized context value
  const value = useMemo(() => ({ layout, scrollContentToTop, registerContentElement }), [layout, scrollContentToTop, registerContentElement]);

  // Render provider
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/**
 * Custom hook to access layout context.
 *
 * @returns The layout context value.
 * @throws Error if used outside of LayoutProvider.
 */
export function useLayout(): LayoutContextType {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayout must be used within a LayoutProvider');
  return ctx;
}
